/**
 * "Ask Lykke to present it" — full-screen presentation with Lykke on a live voice call.
 * She goes page by page (show_page tool), talks each one through, answers questions,
 * jumps back and forth on request and always heads for the end. The listener can also
 * use the arrow keys; Lykke is told and continues from there.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { ChevronLeft, ChevronRight, Ear, Loader2, Mic, MicOff, PhoneOff, X } from 'lucide-react';
import { coworkApi } from '@/cowork/omniad/api/coworkApi';
import { toast } from 'sonner';
import { SlideRenderer, deriveTheme } from '@/cowork/omniad/components/slides/SlideRenderer';
import { useDeckFonts } from '@/cowork/omniad/components/slides/deckFonts';
import { RealtimeCall, RealtimeEars, type RealtimeTool } from '@/cowork/proposals/realtimeCall';
import { LiveCall, pageCommand } from '@/cowork/proposals/liveCall';
import { useQuery } from '@tanstack/react-query';
import { AGENT } from '@/cowork/proposals/ProposalBuilder';
import { CallRecorder } from '@/cowork/proposals/callArchive';
import { voiceErrorText } from '@/cowork/voice/voiceSession';
import { snapshotPage, afterPaint } from './pageSnapshot';

type Status = 'connecting' | 'connected' | 'speaking' | 'listening' | 'tool' | 'ended' | 'error';

export type LykkeMode = 'present' | 'copilot';
/** "Lykke", however the transcriber spells it — the wake word when she sits in on a meeting. */
const WAKE = /\b(hey |hi |ok |okay |so |and )?(lykke|lykka|lykkee|licke|likke|lyke|lika|lickie|lickit|lucky|lyka|lykki)\b/i;
const FOLLOW_UP_MS = 8000;

/** Which voice engine presents (staff can compare): the Realtime call, or GPT-Live (experimental, full duplex). */
export type PresenterEngine = 'realtime' | 'live';
const PREF_ENGINE = 'lykke.presenter.engine';
const PREF_VOICE = 'lykke.presenter.voice';
const VOICES = ['marin', 'cedar'] as const;
function readPref(key: string, fallback: string): string { try { return localStorage.getItem(key) || fallback; } catch { return fallback; } }
function writePref(key: string, v: string) { try { localStorage.setItem(key, v); } catch { /* storage unavailable */ } }


export function LykkePresenter({ presentationId, onClose: onCloseProp, mode = 'present', initialIndex = 0 }: { presentationId: string; /** Gets the page on screen, so the editor stays there. */ onClose: (index?: number) => void; mode?: LykkeMode; initialIndex?: number }) {
  const copilot = mode === 'copilot';
  const earsRef = useRef<RealtimeEars | null>(null);
  const hotUntil = useRef(0);          // after she answers, the room can follow up without the wake word for a few seconds
  const awaitingAnswer = useRef(false);
  const [room, setRoom] = useState<string>('');   // last thing the room said (live)
  const [roomCount, setRoomCount] = useState(0);
  const [data, setData] = useState<{ id: string; title: string; slides: any[]; brand: any } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [index, setIndex] = useState(Math.max(0, initialIndex));
  const [status, setStatus] = useState<Status>('connecting');
  const [caption, setCaption] = useState<{ role: 'kai' | 'user'; text: string } | null>(null);
  const [muted, setMuted] = useState(false);
  const callRef = useRef<RealtimeCall | null>(null);
  const indexRef = useRef(Math.max(0, initialIndex));
  const onClose = useCallback(() => onCloseProp(indexRef.current), [onCloseProp]);
  const totalRef = useRef(0);
  const rootRef = useRef<HTMLDivElement>(null);
  // The page as the listener sees it, captured and shown to Lykke each time it changes (she talks about what is visible).
  const slideRef = useRef<HTMLDivElement>(null);
  const seenPages = useRef(new Set<number>());
  const lastToolMove = useRef(0);
  const continueTimer = useRef<number | null>(null);
  const userTalking = useRef(false);
  const endedRef = useRef(false);
  // 'flow' = Lykke is presenting in order on her own. Off after the listener speaks (a question, a jump) until she turns
  // to the next page herself again — so a page they asked for is never moved away from by the silence cue.
  const flowRef = useRef(true);
  const recorder = useRef<CallRecorder | null>(null);
  const dataRef = useRef<{ id: string; title: string; slides: any[]; brand: any } | null>(null);
  indexRef.current = index;
  // Engine + voice (staff can switch; changing either restarts the call). The co-pilot always runs on Realtime.
  const [engine, setEngine] = useState<PresenterEngine>(() => (readPref(PREF_ENGINE, 'realtime') === 'live' ? 'live' : 'realtime'));
  const [voice, setVoice] = useState<string>(() => readPref(PREF_VOICE, 'marin'));
  const me = useQuery({ queryKey: ['cowork', 'me'], queryFn: () => coworkApi.staffScope.me(), staleTime: 5 * 60_000, retry: false });
  // GPT-Live is a staff-only beta: a client never runs it, whatever a stale preference says.
  const useLive = !copilot && engine === 'live' && me.data?.staff === true;
  const staffKnown = copilot || engine !== 'live' || !me.isPending;
  const liveRef = useRef<LiveCall | null>(null);
  const pagesRef = useRef<string[]>([]);
  const turnsRef = useRef<Array<{ role: 'user' | 'lykke'; text: string }>>([]);
  const delegatingRef = useRef(false);
  const advanceTimer = useRef<number | null>(null);
  const nextAsked = useRef(false);
  const lastUserTurnAt = useRef(0);
  const factsSent = useRef<Set<string>>(new Set());   // facts already given to her this call
  const factsAskedAt = useRef(0);                      // words in the utterance when facts were last asked for

  // Lykke turns the pages herself as she speaks (show_page at the end of each page's talk). This cue is only a
  // slow fallback for the rare turn where she stops without turning: never on the last page, never while the listener talks.
  const falseStartTimer = useRef<number | null>(null);
  const armContinue = useCallback((delay = 7000) => {
    if (continueTimer.current) window.clearTimeout(continueTimer.current);
    continueTimer.current = window.setTimeout(() => {
      continueTimer.current = null;
      if (endedRef.current || userTalking.current || !flowRef.current) return;
      if (indexRef.current >= totalRef.current - 1) return;
      callRef.current?.note(`[Silence: the listener has no question. Bridge and continue: call show_page with page ${indexRef.current + 2} and present it.]`);
      // Safety: if nothing happens (cue swallowed by a response in flight), try again later.
      armContinue(20000);
    }, delay);
  }, []);
  const disarmContinue = useCallback(() => { if (continueTimer.current) { window.clearTimeout(continueTimer.current); continueTimer.current = null; } }, []);

  const theme = useMemo(() => deriveTheme(data?.brand ?? {}), [data?.brand]);
  useDeckFonts(data?.slides, data?.brand?.fontFamily);
  const total = data?.slides.length ?? 0;
  totalRef.current = total;

  /** Show Lykke the page exactly as the listener sees it (once per page): the picture to the Realtime engine; to GPT-Live
   *  (text only) a short description of it from the API. Never blocks her for long and never makes her speak. */
  const showWhatTheySee = useCallback(async (p: number) => {
    if (seenPages.current.has(p) || endedRef.current) return;
    await afterPaint();
    if (indexRef.current !== p || endedRef.current) return; // she or the listener moved on
    const img = await snapshotPage(slideRef.current);
    if (!img) return;
    seenPages.current.add(p);
    const n = totalRef.current;
    if (callRef.current) { callRef.current.see(img, `[What the listener sees right now: page ${p + 1} of ${n}, exactly as it shows on their screen. Talk about what is visible; if something is cut off, empty or missing, do not pretend it is there.]`); return; }
    const live = liveRef.current; if (!live) return;
    const r = await coworkApi.presentations.see(presentationId, { page: p + 1, image: img }).catch(() => null);
    if (r?.seen && liveRef.current === live && !endedRef.current) live.thinking(`What the listener sees on page ${p + 1} (from the screen): ${r.seen}`);
  }, [presentationId]);

  // GPT-Live: the app shows the page and tells her what is on it; she presents it and stops.
  const presentLive = useCallback((i: number, why: 'start' | 'next' | 'jump' | 'keyboard') => {
    const live = liveRef.current; if (!live) return;
    const n = totalRef.current;
    const p = Math.max(0, Math.min(i, Math.max(n - 1, 0)));
    setIndex(p); indexRef.current = p; lastToolMove.current = Date.now();
    live.thinking(`Page ${p + 1} of ${n} is on screen:\n${pagesRef.current[p] || ''}`);
    void showWhatTheySee(p);
    live.instruct(why === 'start' ? `Start now: greet the listener by name, one line on what you will cover, say they can interrupt any time, then present page ${p + 1} (it is on screen).`
      : why === 'next' ? `Page ${p + 1} is now on screen${p === n - 1 ? ': the last page' : ''}. Present it: open with a half-sentence bridge from the previous page.`
      : why === 'jump' ? `The listener asked for page ${p + 1}; it is on screen now. Present it, then wait for them to say go on.`
      : `The listener moved to page ${p + 1}. Present it and continue from there.`);
  }, [showWhatTheySee]);
  const cancelAdvance = useCallback(() => { if (advanceTimer.current) { window.clearTimeout(advanceTimer.current); advanceTimer.current = null; } }, []);
  // She finished talking: turn to the next page after a beat — longer right after the listener spoke (a follow-up may come).
  // `extraMs`: without an audio meter, roughly how long her last words still need to play.
  const scheduleAdvance = useCallback((extraMs = 0) => {
    cancelAdvance();
    const delay = extraMs + (nextAsked.current ? 350 : Date.now() - lastUserTurnAt.current < 12_000 ? 3500 : 1500);
    advanceTimer.current = window.setTimeout(() => {
      advanceTimer.current = null;
      const live = liveRef.current;
      if (!live || endedRef.current || userTalking.current || delegatingRef.current || live.isSpeaking) return;
      if (!flowRef.current && !nextAsked.current) return;
      if (indexRef.current >= totalRef.current - 1) return;
      nextAsked.current = false; flowRef.current = true;
      presentLive(indexRef.current + 1, 'next');
    }, delay);
  }, [cancelAdvance, presentLive]);

  const goTo = useCallback((i: number, byLykke = false) => {
    const clamped = Math.max(0, Math.min(i, Math.max(totalRef.current - 1, 0)));
    if (liveRef.current && !byLykke) { if (clamped === indexRef.current) return; flowRef.current = true; cancelAdvance(); presentLive(clamped, 'keyboard'); return; }
    setIndex(clamped);
    if (!byLykke) void showWhatTheySee(clamped);
    if (byLykke) lastToolMove.current = Date.now();
    else if (copilot) callRef.current?.note(`[Page ${clamped + 1} of ${totalRef.current} is on screen${dataRef.current?.slides?.[clamped]?.title ? `: ${String(dataRef.current.slides[clamped].title).slice(0, 120)}` : ''}]`, false);
    else if (Date.now() - lastToolMove.current > 800) callRef.current?.note(`[The listener moved to page ${clamped + 1} of ${totalRef.current} with the keyboard. Present this page now and continue from here.]`);
  }, [cancelAdvance, presentLive, showWhatTheySee]);

  // Load the deck, then start the call.
  useEffect(() => {
    let cancelled = false;
    let call: RealtimeCall | null = null;
    // A new call (first start, or an engine / voice switch): the previous call's "ended" must not close the presenter.
    if (!staffKnown) return;
    endedRef.current = false; setStatus('connecting'); setError(null);
    (async () => {
      try {
        const d = await coworkApi.presentations.get(presentationId) as any;
        if (cancelled) return;
        setData(d); dataRef.current = d;
        totalRef.current = d.slides?.length || 0;
        if (useLive) {
          let userName: string | null = null;
          const live = new LiveCall({
            onStatus: (st) => { if (cancelled) return; setStatus(st); if (st === 'ended') { endedRef.current = true; cancelAdvance(); void recorder.current?.archive(); } },
            onTranscript: (role, text, final) => {
              if (cancelled) return;
              if (text) setCaption({ role, text });
              // Facts for what the listener is asking: from six words on (a head start), and once more when they finish.
              if (role === 'user' && text) {
                const words = text.split(/\s+/).filter(Boolean).length;
                if ((final && words >= 3) || (!final && words >= 6 && words - factsAskedAt.current >= 6)) {
                  factsAskedAt.current = final ? 0 : words;
                  const recent = turnsRef.current.slice(-4).map((t) => `${t.role === 'user' ? 'Listener' : 'Lykke'}: ${t.text}`).join('\n');
                  coworkApi.presentations.liveContext(presentationId, { utterance: text, recent, page: indexRef.current + 1 })
                    .then((r) => {
                      if (cancelled) return;
                      const fresh = r.facts.filter((f) => !factsSent.current.has(f.text));
                      if (!fresh.length) return;
                      for (const f of fresh) factsSent.current.add(f.text);
                      live.thinking(`Facts for the listener (${text.slice(0, 80)}):\n${fresh.map((f) => `- ${f.label}: ${f.text}`).join('\n')}`);
                    })
                    .catch(() => { /* she can still delegate */ });
                }
              }
              if (!final || !text) return;
              // No voice meter (a suspended audio context): her finished turn is the cue to move on.
              // The transcript runs ahead of her audio: allow ~60 ms per character still to be spoken.
              if (role === 'kai' && !live.meterWorks) scheduleAdvance(Math.min(20_000, text.length * 60));
              recorder.current?.add(role === 'kai' ? 'agent' : 'user', text);
              turnsRef.current = [...turnsRef.current, { role: (role === 'kai' ? 'lykke' : 'user') as 'user' | 'lykke', text }].slice(-16);
              if (role !== 'user') return;
              lastUserTurnAt.current = Date.now();
              setTimeout(() => setCaption((c) => (c?.role === 'user' ? null : c)), 4000);
              const cmd = pageCommand(text, indexRef.current);
              if (cmd?.kind === 'jump') { flowRef.current = false; cancelAdvance(); presentLive(cmd.page, 'jump'); }
              else if (cmd?.kind === 'next') { flowRef.current = true; nextAsked.current = true; if (!liveRef.current?.isSpeaking) scheduleAdvance(); }
              else {
                // Anything else may still ask for a page ("back to the first slide", "show me the budget"): the page-intent model decides.
                const asked = indexRef.current;
                coworkApi.presentations.liveNav(presentationId, { utterance: text, recent: turnsRef.current.slice(-6), page: asked + 1 })
                  .then((r) => {
                    if (cancelled || r.action !== 'goto' || !r.page || indexRef.current !== asked) return;
                    const to = r.page - 1;
                    if (to === asked + 1) { flowRef.current = true; cancelAdvance(); presentLive(to, 'next'); }
                    else { flowRef.current = false; cancelAdvance(); presentLive(to, 'jump'); }
                  })
                  .catch(() => { /* no page change */ });
              }
            },
            onSpeaking: (sp) => { if (cancelled) return; if (sp) cancelAdvance(); else scheduleAdvance(); },
            // The listener stopped: if she is quiet too (a stray "mm-hm", noise), the deck must not stall.
            onUserSpeaking: (sp) => { if (cancelled) return; userTalking.current = sp; if (sp) cancelAdvance(); else if (!live.isSpeaking && live.meterWorks) scheduleAdvance(); },
            onDelegation: (id) => {
              if (cancelled) return;
              delegatingRef.current = true;
              live.flushUser(); // the question itself: the delegation arrives before the transcript settles
              cancelAdvance();  // after the flush, which may have scheduled one
              coworkApi.presentations.liveAnswer(presentationId, { transcript: turnsRef.current, page: indexRef.current + 1 })
                .then((r) => { if (!cancelled) live.commentary(r.answer, id); })
                .catch(() => { if (!cancelled) live.commentary("I don't have that in front of me: I'll follow up after the call.", id); })
                .finally(() => { delegatingRef.current = false; });
            },
            onError: (m) => { if (!cancelled) setError(m); },
            // Her minutes for this call ran out (or the server ended it): LiveCall shows the calm line as a toast,
            // which stays while the presenter closes.
          });
          liveRef.current = live;
          // The route answers the SDP plus the voice session (voiceSessionId, endsAt): LiveCall keeps it (voiceSession.ts).
          await live.connect(async (sdp) => {
            const s = await coworkApi.presentations.liveSession(presentationId, { sdp, voice });
            pagesRef.current = s.pages; userName = s.userName;
            return s;
          });
          if (cancelled || !live.active) { live.end(); return; }
          recorder.current = new CallRecorder({ kind: 'presentation', agent: 'Lykke', title: d.title, brandId: d.brandId || d.brand?.id, refId: presentationId, userName, context: (d.slides || []).map((sl: any, i: number) => `${i + 1}. ${sl.title || sl.eyebrow || sl.type}`).join('\n') });
          recorder.current.addVoiceSession(live.voiceSessionId);
          // A restart (engine / voice switch) picks up on the page on screen.
          presentLive(Math.max(0, indexRef.current), 'start');
          return;
        }
        // The recorder learns the caller's name and the voice session from the mint answer (the offer goes with it).
        const rec = new CallRecorder({ kind: copilot ? 'meeting' : 'presentation', agent: 'Lykke', title: d.title, brandId: d.brandId || d.brand?.id, refId: presentationId, context: (d.slides || []).map((sl: any, i: number) => `${i + 1}. ${sl.title || sl.eyebrow || sl.type}`).join('\n') });
        recorder.current = rec;
        const tools: RealtimeTool[] = [
          {
            name: 'show_page', description: 'Show a page of the deck to the listener. Call this BEFORE talking about a page. Pages are numbered from 1.',
            parameters: { type: 'object', properties: { page: { type: 'integer', description: 'Page number, 1-based' } }, required: ['page'] },
            execute: async (args) => {
              const p = Math.max(1, Math.min(Number(args.page) || 1, totalRef.current));
              flowRef.current = p === indexRef.current + 2 || p === indexRef.current + 1; // next page (or same) = her own flow; a jump = the listener's request
              // She generates speech much faster than it plays. Holding the tool RESULT until her queued audio is about
              // to end means she cannot start the next page's talk early. The flip lands ~1.5 s before her last words
              // on the page end (predicted from her speaking rate), so the bridge overlaps the turn. (Nothing playing → immediate.)
              await new Promise<void>((resolve) => { if (callRef.current) callRef.current.beforeAudioEnd(1500, resolve); else resolve(); });
              if (endedRef.current) return { ok: false };
              goTo(p - 1, true); indexRef.current = p - 1;
              // She sees the page as the listener does before she talks about it (at most ~1.5 s).
              await Promise.race([showWhatTheySee(p - 1), new Promise((r) => setTimeout(r, 1500))]);
              return { ok: true, showing: p, of: totalRef.current, last: p === totalRef.current };
            },
          },
          {
            name: 'end_presentation', description: 'End the presentation after the listener is done and you have said goodbye.',
            parameters: { type: 'object', properties: {} },
            execute: async () => { setTimeout(() => { callRef.current?.end(); }, 2500); return { ok: true }; },
          },
        ];
        call = new RealtimeCall(tools, {
          onStatus: (s) => {
            if (cancelled) return;
            setStatus(s);
            if (s === 'ended') { endedRef.current = true; earsRef.current?.end(); void recorder.current?.archive(); }
            if (copilot) return;
            if (s === 'listening') armContinue(); else disarmContinue();
          },
          onTranscript: (role, text, final) => {
            if (role === 'user') {
              userTalking.current = !final; disarmContinue();
              if (falseStartTimer.current) window.clearTimeout(falseStartTimer.current);
              if (final) { flowRef.current = false; disarmContinue(); }
              // Noise or her own audio can trip "speech started" with no words behind it — clear it after a few seconds.
              else falseStartTimer.current = window.setTimeout(() => { userTalking.current = false; armContinue(4000); }, 6000);
            }
            if (final && text) recorder.current?.add(role === 'kai' ? 'agent' : 'user', text);
            if (text) setCaption({ role, text });
            if (final && role === 'user') setTimeout(() => setCaption((c) => (c?.role === 'user' ? null : c)), 4000);
          },
          onError: (m) => setError(m),
          // Minutes run out (or the server ended the call): RealtimeCall shows the calm line as a toast, the call ends.
          onAudio: (playing) => {
            if (!copilot) return;
            // Her voice must not come back in as "the room": pause the ears while she speaks, reopen the follow-up window after.
            earsRef.current?.setMuted(playing);
            if (!playing) { awaitingAnswer.current = false; hotUntil.current = Date.now() + FOLLOW_UP_MS; }
          },
        });
        callRef.current = call;
        const rtCall = call;
        // The offer goes to the mint route: the server connects the call and keeps its voice session (voiceSession.ts).
        const secret = await call.connect((sdp) => coworkApi.presentations.presentSession(presentationId, { mode, voice, sdp }), undefined, (s) => {
          rec.setUserName(s.userName); rec.addVoiceSession(rtCall.voiceSessionId);
          if (s.warning) toast.warning(s.warning, { duration: 9000 });
          const who = s.userName ? `Hi ${s.userName}` : 'Hi';
          return copilot
            ? { instructions: s.instructions, voice: s.voice, silent: true }
            : { instructions: s.instructions, voice: s.voice, opening: `Start now: say "${who}, I'm Lykke: I'll walk you through this in a few minutes, interrupt me any time." Then call show_page with page 1 and present page 1.` };
        });
        if (cancelled || !call.active || !secret) return;
        if (copilot) {
          // Her own mic feed stays silent — the room reaches her as text from the ears session, so her VAD never fires on its own.
          call.setMuted(true);
          const ears = new RealtimeEars({
            onUtterance: (text, final) => {
              if (endedRef.current) return;
              setRoom(text);
              if (!final) return;
              recorder.current?.add('room', text); setRoomCount((n) => n + 1);
              const page = indexRef.current + 1;
              const addressed = WAKE.test(text) || (Date.now() < hotUntil.current && !awaitingAnswer.current);
              if (addressed) {
                awaitingAnswer.current = true; hotUntil.current = 0;
                callRef.current?.note(`[Someone in the room is asking YOU directly (page ${page} is on screen). Answer briefly, then go quiet.] ${text}`, true);
              } else {
                callRef.current?.note(`[Room, page ${page}]: ${text}`, false);
              }
            },
            onError: (m) => toast.error(`Ears: ${m}`),
            // The ears' minutes ran out: the whole meeting call stops with the calm line.
            onTimeUp: (m) => { toast(m); callRef.current?.end(); },
          });
          earsRef.current = ears;
          // The ears are their own voice session (kind 'ears'): the offer goes to the mint route too.
          await ears.connect((sdp) => coworkApi.calls.earsSecret({ sdp }));
          if (cancelled || !ears.active) return;
          rec.addVoiceSession(ears.voiceSessionId);
          setStatus('listening');
          call.note(`[Meeting started. ${secret.userName || 'A Beatly colleague'} is presenting "${d.title}" (${d.slides?.length || 0} pages). Page 1 is on screen. Stay silent until someone says your name.]`, false);
        }
      } catch (e) {
        if (!cancelled) { setError(voiceErrorText(e)); setStatus('error'); }
      }
    })();
    return () => { cancelled = true; disarmContinue(); cancelAdvance(); if (falseStartTimer.current) window.clearTimeout(falseStartTimer.current); earsRef.current?.end(); call?.end(); liveRef.current?.end(); liveRef.current = null; void recorder.current?.archive(); };
  }, [presentationId, mode, copilot, goTo, armContinue, disarmContinue, useLive, voice, cancelAdvance, scheduleAdvance, presentLive, staffKnown]);

  // Full screen while presenting; keyboard for the listener.
  useEffect(() => {
    const el = rootRef.current;
    el?.requestFullscreen?.().catch(() => {});
    const onKey = (e: KeyboardEvent) => {
      liveRef.current?.resumeAudio();
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
      else if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); goTo(indexRef.current + 1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(indexRef.current - 1); }
    };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {}); };
  }, [goTo, onClose]);

  useEffect(() => { if (status === 'ended' && endedRef.current) { const t = setTimeout(onClose, 600); return () => clearTimeout(t); } }, [status, onClose]);

  const toggleMute = () => {
    setMuted((m) => { const next = !m; if (copilot) earsRef.current?.setMuted(next); else { callRef.current?.setMuted(next); liveRef.current?.setMuted(next); } return next; });
  };
  // Staff compare engines and voices; a change restarts the call from the page on screen.
  const pickEngine = (e: PresenterEngine) => { writePref(PREF_ENGINE, e); setCaption(null); setEngine(e); };
  const pickVoice = (v: string) => { writePref(PREF_VOICE, v); setCaption(null); setVoice(v); };

  const statusLabel = status === 'connecting' ? 'Connecting…' : copilot ? (status === 'speaking' ? 'Answering' : status === 'error' ? 'Call failed' : status === 'ended' ? 'Meeting ended' : muted ? 'Not listening' : 'Listening in') : status === 'speaking' ? 'Presenting' : status === 'listening' ? 'Listening' : status === 'tool' ? 'Turning the page' : status === 'error' ? 'Call failed' : status === 'ended' ? 'Call ended' : 'On the call';

  return (
    <div ref={rootRef} role="dialog" aria-modal="true" onPointerDown={() => liveRef.current?.resumeAudio()} style={{ position: 'fixed', inset: 0, zIndex: 1200, background: '#07070d', display: 'flex', flexDirection: 'column', color: '#fff' }}>
      {/* Slide */}
      <div style={{ flex: 1, minHeight: 0, display: 'grid', placeItems: 'center', padding: '28px 32px 8px' }}>
        <div ref={slideRef} style={{ position: 'relative', width: 'min(100%, calc((100vh - 150px) * 16 / 9))', aspectRatio: '16 / 9', background: theme.bg, borderRadius: 14, overflow: 'hidden', boxShadow: '0 30px 80px rgba(0,0,0,0.6)', containerType: 'size' } as CSSProperties}>
          {data?.slides[index] && <SlideRenderer slide={data.slides[index]} index={index} theme={theme} title={data.title} />}
          {!data && !error && <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: '#98a2b3' }}><Loader2 className="h-7 w-7 animate-spin" /></div>}
          {error && <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', padding: 40, textAlign: 'center', color: '#fda29b', fontSize: 15 }}>{error}</div>}
        </div>
      </div>

      {/* Bottom bar: Lykke, captions, controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '10px 28px 18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, flex: 1 }}>
          <div style={{ position: 'relative', width: 64, height: 64, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, boxShadow: status === 'speaking' ? '0 0 0 4px rgba(190,67,255,0.75), 0 0 28px rgba(190,67,255,0.55)' : status === 'listening' ? '0 0 0 4px rgba(16,185,129,0.6)' : '0 0 0 3px rgba(255,255,255,0.15)', transition: 'box-shadow 200ms' }}>
            <video src={AGENT.video} poster={AGENT.photo} autoPlay loop muted playsInline aria-label={AGENT.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600 }}>
              {AGENT.name}
              <span style={{ fontSize: 11, fontWeight: 500, color: status === 'error' ? '#fda29b' : 'rgba(255,255,255,0.6)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {status === 'connecting' && <Loader2 className="h-3 w-3 animate-spin" />}{statusLabel}
              </span>
            </div>
            <div style={{ fontSize: 15, lineHeight: 1.35, color: caption?.role === 'user' ? 'rgba(255,255,255,0.7)' : '#fff', marginTop: 4, maxWidth: 820, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' } as CSSProperties}>
              {copilot
                ? (status === 'speaking' && caption?.role === 'kai' ? caption.text : room ? <span style={{ color: 'rgba(255,255,255,0.6)' }}>Room: {room}</span> : (status === 'connecting' ? 'Lykke is joining the meeting…' : 'You present. Lykke follows along and takes notes: say “Lykke, …” to ask her anything.'))
                : caption ? (caption.role === 'user' ? `You: ${caption.text}` : caption.text) : (status === 'connecting' ? 'Preparing the deck for Lykke…' : 'Just talk: Lykke hears you. Ask anything, or say "go back" / "skip ahead".')}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <button onClick={() => goTo(index - 1)} disabled={index === 0} title="Previous page" style={btn()}><ChevronLeft className="h-4 w-4" /></button>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', minWidth: 56, textAlign: 'center' }}>{total ? `${index + 1} / ${total}` : ''}</div>
          <button onClick={() => goTo(index + 1)} disabled={index >= total - 1} title="Next page" style={btn()}><ChevronRight className="h-4 w-4" /></button>
          <button onClick={toggleMute} title={muted ? 'Unmute' : 'Mute'} style={btn(muted ? '#f04438' : undefined)}>{muted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}</button>
          <button onClick={onClose} title="End" style={{ ...btn('#f04438'), width: 'auto', padding: '0 14px', gap: 6, fontSize: 12, fontWeight: 600 }}><PhoneOff className="h-4 w-4" /> End</button>
          <button onClick={onClose} title="Close" style={btn()}><X className="h-4 w-4" /></button>
        </div>
      </div>

      {!copilot && me.data?.staff && (
        <div style={{ position: 'absolute', top: 10, left: 20, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, padding: '4px 8px', borderRadius: 999, background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.8)' }} title="Compare the voice engines (staff only)">
          <select aria-label="Voice engine" value={engine} onChange={(e) => pickEngine(e.target.value as PresenterEngine)} style={sel}>
            <option value="realtime">Realtime (current)</option>
            <option value="live">GPT-Live (beta)</option>
          </select>
          <select aria-label="Voice" value={voice} onChange={(e) => pickVoice(e.target.value)} style={sel}>
            {VOICES.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
      )}

      {copilot && status !== 'connecting' && status !== 'error' && (
        <div style={{ position: 'absolute', top: 12, right: 20, display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 11.5, fontWeight: 600, padding: '6px 12px', borderRadius: 999, background: 'rgba(16,185,129,0.16)', color: '#6ee7b7', border: '1px solid rgba(16,185,129,0.35)' }}>
          <Ear className="h-3.5 w-3.5" /> Lykke is listening and taking notes{roomCount ? ` · ${roomCount}` : ''}
        </div>
      )}

      {/* Dots */}
      {total > 1 && (
        <div style={{ position: 'absolute', top: 12, left: 0, right: 0, display: 'flex', gap: 5, justifyContent: 'center' }}>
          {data!.slides.map((_, i) => <button key={i} onClick={() => goTo(i)} aria-label={`Page ${i + 1}`} style={{ width: i === index ? 22 : 7, height: 7, borderRadius: 4, border: 'none', background: i === index ? '#fff' : 'rgba(255,255,255,0.25)', cursor: 'pointer', padding: 0, transition: 'all 150ms' }} />)}
        </div>
      )}
    </div>
  );
}

const sel: CSSProperties = { background: 'transparent', color: '#fff', border: 'none', fontSize: 11.5, cursor: 'pointer', outline: 'none' };

function btn(bg?: string): CSSProperties {
  return { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 38, height: 38, borderRadius: 999, border: 'none', background: bg || 'rgba(255,255,255,0.1)', color: '#fff', cursor: 'pointer' };
}
